import { useCallback } from 'react';

interface CommandArgument {
  name: string;
  description: string;
  placeholder?: string;
  required?: boolean;
  type?: string;
}

interface Command {
  name: string;
  description: string;
  arguments?: CommandArgument[];
}

interface UseKeyboardHandlerProps {
  handleTabPress: () => string | null;
  setQuestion: (question: string) => void;
  showCommandSuggestions: boolean;
  commandSuggestions: Command[];
  showMessageHistory: boolean;
  setShowMessageHistory: (show: boolean) => void;
  hideCommandSuggestions: () => void;
  getLastUserMessages: () => Array<{ question: string }>;
  selectedHistoryIndex: number;
  setSelectedHistoryIndex: (index: number) => void;
  loading: boolean;
  question: string;
  handleAsk: () => void;
}

export const useKeyboardHandler = ({
  handleTabPress,
  setQuestion,
  showCommandSuggestions,
  commandSuggestions,
  showMessageHistory,
  setShowMessageHistory,
  hideCommandSuggestions,
  getLastUserMessages,
  selectedHistoryIndex,
  setSelectedHistoryIndex,
  loading,
  question,
  handleAsk,
}: UseKeyboardHandlerProps) => {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Handle tab key for autocomplete
      if (e.key === 'Tab') {
        e.preventDefault();
        const completedValue = handleTabPress();
        if (completedValue) {
          setQuestion(completedValue);
        }
      }
      // Handle up arrow for command suggestions
      else if (e.key === 'ArrowUp' && showCommandSuggestions && commandSuggestions.length > 0) {
        e.preventDefault();
        // Let the CommandSuggestions component handle the navigation
        return;
      }
      // Handle down arrow for command suggestions
      else if (e.key === 'ArrowDown' && showCommandSuggestions && commandSuggestions.length > 0) {
        e.preventDefault();
        // Let the CommandSuggestions component handle the navigation
        return;
      }
      // Handle up arrow for message history
      else if (e.key === 'ArrowUp' && showMessageHistory) {
        e.preventDefault();
        const lastMessages = getLastUserMessages();
        if (lastMessages.length > 0) {
          setSelectedHistoryIndex(Math.max(0, selectedHistoryIndex - 1)); // Loop to last
        }
      }
      // Handle down arrow for message history
      else if (e.key === 'ArrowDown' && showMessageHistory) {
        e.preventDefault();
        const lastMessages = getLastUserMessages();
        if (lastMessages.length > 0) {
          setSelectedHistoryIndex(Math.min(lastMessages.length - 1, selectedHistoryIndex + 1)); // Loop to first
        }
      }
      // Handle up arrow to open message history when input is empty
      else if (e.key === 'ArrowUp' && !showMessageHistory && question === '') {
        e.preventDefault();
        const lastMessages = getLastUserMessages();
        if (lastMessages.length > 0) {
          setShowMessageHistory(true);
          setSelectedHistoryIndex(0);
        }
      }
      // Handle escape key to close dropdowns
      else if (e.key === 'Escape') {
        setShowMessageHistory(false);
        hideCommandSuggestions();
      }
      // Handle enter key for sending messages
      else if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (showMessageHistory) {
          // Select the highlighted message from history
          const lastMessages = getLastUserMessages();
          if (lastMessages.length > 0 && selectedHistoryIndex < lastMessages.length) {
            setQuestion(lastMessages[selectedHistoryIndex].question);
            setShowMessageHistory(false);
          }
        } else if (showCommandSuggestions && commandSuggestions.length > 0) {
          // Command suggestions are visible, let them handle the Enter key
          // The CommandSuggestions component will handle selecting the highlighted command
          return;
        } else if (!loading && question) {
          handleAsk();
        }
      }
    },
    [
      handleTabPress,
      setQuestion,
      showCommandSuggestions,
      commandSuggestions,
      showMessageHistory,
      setShowMessageHistory,
      hideCommandSuggestions,
      getLastUserMessages,
      selectedHistoryIndex,
      setSelectedHistoryIndex,
      loading,
      question,
      handleAsk,
    ],
  );

  return {
    handleKeyDown,
  };
};
